import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableColumnGroups} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {getCustomColumnDataTypeDisplayName} from '../custom-columns/CustomColumnConstants';

/**
 * Each column type has a matching data type tooltip.
 */
interface TooltipMap {
  [type: string]: string | null;
}

/**
 * This is the list of data type tooltips used in structured tables.
 */
const TOOLTIP_ALIAS = 'Alias';
const TOOLTIP_ARCHIVED = 'Archived';
const TOOLTIP_ATTACHMENT = 'Attachment';
const TOOLTIP_BATCH = 'Batch';
const TOOLTIP_BIOENTITY = 'Entity';
const TOOLTIP_BOOLEAN = 'Boolean';
const TOOLTIP_BOX = 'Box';
export const TOOLTIP_COMPUTED_FIELD = 'Computed field';
const TOOLTIP_CONTAINER = 'Container';
export const TOOLTIP_CONVERTED_FROM_FIELD = 'Converted from another item';
const TOOLTIP_CUSTOM_HINT = '(Custom Column)';
const TOOLTIP_DATE = 'Date';
const TOOLTIP_DATETIME = 'Date and time';
const TOOLTIP_DELETED = 'Deleted';
const TOOLTIP_DNA = 'DNA';
const TOOLTIP_DROPDOWN = 'Dropdown'; // used when selecting from a schematized selector dropdown
const TOOLTIP_ENTRY = 'Entry';
const TOOLTIP_JSON = 'JSON';
const TOOLTIP_LOOKUP = 'Lookup';
const TOOLTIP_MULTISELECT = 'Select'; // used when selecting from preset options
const TOOLTIP_NUMBER = 'Number';
export const TOOLTIP_PARTIES = 'Users or teams';
const TOOLTIP_PLATE = 'Plate';
const TOOLTIP_POSITION = 'Position';
const TOOLTIP_PROCESS_OUTPUT = 'Output';
const TOOLTIP_PROCESS_TASK = 'Task';
export const TOOLTIP_RESTRICTED_SAMPLE_USERS = getRestrictedSampleUsersHeaderText();
export const TOOLTIP_SAMPLE_OWNERS = 'Sample owners';
export const TOOLTIP_SNAPSHOT_FIELD = 'Computed field (snapshot)';
const TOOLTIP_STAGE_RUN = 'Stage run';
const TOOLTIP_STUDY = 'Study';
const TOOLTIP_STORABLE = 'Inventory';
const TOOLTIP_TEXT = 'Text';
const TOOLTIP_USER = 'User';

/**
 * Label tooltip mappings for table column types.
 */
const NON_TAG_VALUE_ICONS: TooltipMap = {
  [TableColumnType.ALIAS]: TOOLTIP_ALIAS,
  [TableColumnType.BATCH]: TOOLTIP_BATCH,
  [TableColumnType.BIOENTITY_NAME]: TOOLTIP_TEXT,
  [TableColumnType.BOOLEAN]: TOOLTIP_BOOLEAN,
  [TableColumnType.BOX]: TOOLTIP_BOX,
  [TableColumnType.CONCENTRATION]: TOOLTIP_NUMBER,
  [TableColumnType.CONTAINERS]: TOOLTIP_CONTAINER,
  [TableColumnType.DEDICATED_LOOKUP]: TOOLTIP_LOOKUP,
  [TableColumnType.DNA_BASES]: TOOLTIP_DNA,
  [TableColumnType.DNA_HELM]: TOOLTIP_DNA,
  [TableColumnType.DNA_IDT]: TOOLTIP_DNA,
  [TableColumnType.EXISTING_BIOENTITY]: TOOLTIP_BIOENTITY,
  [TableColumnType.FULFILLABLE_STATUS]: TOOLTIP_MULTISELECT,
  [TableColumnType.INGREDIENT_CATALOG_ID]: TOOLTIP_TEXT,
  [TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT]: TOOLTIP_TEXT,
  [TableColumnType.INGREDIENT_NOTES]: TOOLTIP_TEXT,
  [TableColumnType.LOOKUP_CONFIG]: TOOLTIP_LOOKUP,
  [TableColumnType.MEASUREMENT_AMOUNT]: TOOLTIP_NUMBER,
  [TableColumnType.NUMBER_OF_CONTAINERS]: TOOLTIP_NUMBER,
  [TableColumnType.OPTION_SEARCH_SELECTOR]: TOOLTIP_MULTISELECT,
  [TableColumnType.OPTION_SELECTOR]: TOOLTIP_MULTISELECT,
  [TableColumnType.PARENT_STORABLE]: TOOLTIP_STORABLE,
  // Default to TOOLTIP_PARTIES. Check for column group in columnDefinitionToTooltip
  [TableColumnType.PARTIES]: TOOLTIP_PARTIES,
  [TableColumnType.PLATE_LAYOUT_SELECTOR]: TOOLTIP_MULTISELECT,
  [TableColumnType.PLATE]: TOOLTIP_PLATE,
  [TableColumnType.POSITION]: TOOLTIP_POSITION,
  [TableColumnType.PROCESS_OUTPUT]: TOOLTIP_PROCESS_OUTPUT,
  [TableColumnType.PROCESS_TASK_ASSIGNEE]: TOOLTIP_USER,
  [TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN]: TOOLTIP_ENTRY,
  [TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE]: TOOLTIP_DATETIME,
  [TableColumnType.PROCESS_TASK_STATUS]: TOOLTIP_MULTISELECT,
  [TableColumnType.PROCESS_TASK]: TOOLTIP_PROCESS_TASK,
  [TableColumnType.QUANTITY]: TOOLTIP_NUMBER,
  [TableColumnType.REQUEST_TASK_SELECTION]: TOOLTIP_MULTISELECT,
  [TableColumnType.RNA_BASES]: TOOLTIP_DNA,
  [TableColumnType.RNA_HELM]: TOOLTIP_DNA,
  [TableColumnType.RNA_IDT]: TOOLTIP_DNA,
  [TableColumnType.SAMPLE_GROUP_SAMPLE]: TOOLTIP_BIOENTITY,
  [TableColumnType.SAMPLE_RESTRICTION_STATUS]: TOOLTIP_MULTISELECT,
  [TableColumnType.SAMPLE]: TOOLTIP_BIOENTITY,
  [TableColumnType.STAGE_RUN_SAMPLE]: TOOLTIP_BIOENTITY,
  [TableColumnType.STAGE_RUN]: TOOLTIP_STAGE_RUN,
  [TableColumnType.STRING]: TOOLTIP_TEXT,
  [TableColumnType.STUDIES]: TOOLTIP_STUDY,
  [TableColumnType.UNIT_SELECTOR]: TOOLTIP_MULTISELECT,
  // Default to archived tooltip, though we check for whether the field was deleted or archived in
  // `columnDefinitionToTooltip`
  [TableColumnType.MISSING_FIELD]: TOOLTIP_ARCHIVED,
};

/**
 * Label tooltip mappings for when column type is declared by a schema field.
 */
const TAG_VALUE_TYPE_ICONS: TooltipMap = {
  [FieldType.AA_PART_LINK]: TOOLTIP_BIOENTITY,
  [FieldType.ASSAY_REQUEST_LINK]: TOOLTIP_BIOENTITY,
  [FieldType.ASSAY_RESULT_LINK]: TOOLTIP_BIOENTITY,
  [FieldType.ASSAY_RUN_LINK]: TOOLTIP_BIOENTITY,
  [FieldType.BATCH_LINK]: TOOLTIP_BATCH,
  [FieldType.BIOENTITY_LINK]: TOOLTIP_BIOENTITY,
  [FieldType.BLOB_LINK]: TOOLTIP_ATTACHMENT,
  [FieldType.BOOLEAN]: TOOLTIP_BOOLEAN,
  [FieldType.DATE]: TOOLTIP_DATE,
  [FieldType.DATETIME]: TOOLTIP_DATETIME,
  [FieldType.ENTRY_LINK]: TOOLTIP_ENTRY,
  [FieldType.FLOAT]: TOOLTIP_NUMBER,
  [FieldType.INTEGER]: TOOLTIP_NUMBER,
  [FieldType.JSON]: TOOLTIP_JSON,
  [FieldType.LONG_TEXT]: TOOLTIP_TEXT,
  [FieldType.PART_LINK]: TOOLTIP_BIOENTITY,
  [FieldType.SAMPLE_LINK]: TOOLTIP_BIOENTITY,
  [FieldType.SELECTOR]: TOOLTIP_DROPDOWN,
  [FieldType.STORABLE_LINK]: TOOLTIP_STORABLE,
  [FieldType.TEXT]: TOOLTIP_TEXT,
  [FieldType.TRANSCRIPTION_LINK]: TOOLTIP_BIOENTITY,
  [FieldType.TRANSLATION_LINK]: TOOLTIP_BIOENTITY,
};

/**
 *
 * @param columnDefinition the column to find a tooltip for.
 * @returns the tooltip title text or null if no tooltip was found for the column def.
 */
export default function columnDefinitionToTooltip(columnDefinition: ColumnDefinition): string | null {
  if (columnDefinition.type === TableColumnType.MEASUREMENT_AMOUNT && columnDefinition.isComputed) {
    return TOOLTIP_COMPUTED_FIELD;
  }

  if (columnDefinition.type === TableColumnType.TAG_VALUE) {
    const {
      tagSchemaFieldType: fieldType,
      tagSchemaFieldDescription: description,
      tagSchemaFieldDecimalPrecision: decimalPrecision,
    } = columnDefinition;
    let type: string | null;
    if (columnDefinition.tagSchemaFieldIsComputed) {
      type = TOOLTIP_COMPUTED_FIELD;
    } else if (columnDefinition.tagSchemaFieldIsConvertedFromLink) {
      type = TOOLTIP_CONVERTED_FROM_FIELD;
    } else if (columnDefinition.tagSchemaFieldIsSnapshot) {
      type = TOOLTIP_SNAPSHOT_FIELD;
    } else {
      type = TAG_VALUE_TYPE_ICONS[fieldType];
    }

    let enhancedType = type;
    // add decimal precision to the tooltip if set
    if (decimalPrecision != null) {
      enhancedType += ` (Schema Display Precision: ${decimalPrecision})`;
    }

    // add a description to the tooltip if available
    if (description) {
      return `[${enhancedType}] ${description}`;
    } else {
      return enhancedType;
    }
  }

  if (columnDefinition.type === TableColumnType.MISSING_FIELD) {
    return columnDefinition.isArchived ? TOOLTIP_ARCHIVED : TOOLTIP_DELETED;
  }

  if (columnDefinition.type === TableColumnType.CUSTOM) {
    return `${getCustomColumnDataTypeDisplayName(
      columnDefinition.customColumnConfig.dataType
    )} ${TOOLTIP_CUSTOM_HINT} `;
  }

  if (columnDefinition.type === TableColumnType.PARTIES) {
    return columnDefinition.columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS
      ? TOOLTIP_SAMPLE_OWNERS
      : columnDefinition.columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS
        ? TOOLTIP_RESTRICTED_SAMPLE_USERS
        : TOOLTIP_PARTIES;
  }

  return NON_TAG_VALUE_ICONS[columnDefinition.type] ?? null;
}
